import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getRedemptionStatement, getSchemes } from "../../../api/bseApi";

/**
 * Format date to DD/MM/YYYY for BSE
 */
function formatDateForBse(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Get default date range (last 30 days)
 */
function getDefaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    fromDate: from.toISOString().split("T")[0],
    toDate: to.toISOString().split("T")[0],
  };
}

/**
 * Parse BSE Redemption Statement response (pipe-separated rows)
 * OrderNo|SchemeName|FolioNo|RedemptionAmount|UnitsRedeemed|NAV|NavDate|PayoutDate|CreditedBankAccount
 */
function parseRedemptionResponse(rawData) {
  if (!rawData || typeof rawData !== "string") return [];
  const rows = rawData.split(/\r?\n/).filter((r) => r && r.trim());
  const records = [];
  for (const row of rows) {
    const parts = row.split("|").map((p) => (p || "").trim());
    records.push({
      orderNo: parts[0] || "-",
      schemeName: parts[1] || "-",
      folioNo: parts[2] || "-",
      redemptionAmount: parts[3] || "-",
      unitsRedeemed: parts[4] || "-",
      nav: parts[5] || "-",
      navDate: parts[6] || "-",
      payoutDate: parts[7] || "-",
      creditedBankAccount: parts[8] || "-",
    });
  }
  return records;
}

/**
 * RedemptionStatementScreen - BSE StAR MF Redemption Statement (MFAPI Flag 04)
 * Opens from Reports/Tracking or Order Status screen
 * Receives: ClientCode (mandatory), Optional: Date range, Scheme
 */
export default function RedemptionStatementScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const state = location.state;
  const [context, setContext] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [filters, setFilters] = useState(() => {
    const def = getDefaultDateRange();
    return { fromDate: def.fromDate, toDate: def.toDate, schemeCd: "" };
  });
  const [filterErrors, setFilterErrors] = useState({});

  const clientCode = state?.clientCode || new URLSearchParams(location.search).get("clientCode");

  const fetchRedemptions = useCallback(async () => {
    if (!context?.clientCode) return null;
    const { fromDate, toDate, schemeCd } = filters;
    if (!fromDate || !toDate) return null;
    try {
      const payload = {
        clientCode: context.clientCode,
        fromDate: formatDateForBse(fromDate),
        toDate: formatDateForBse(toDate),
      };
      if (schemeCd) payload.schemeCd = schemeCd;
      const res = await getRedemptionStatement(payload);
      if (res.data?.status === 1) {
        return parseRedemptionResponse(res.data?.data);
      }
      return null;
    } catch (err) {
      console.warn("Redemption fetch failed:", err);
      return null;
    }
  }, [context?.clientCode, filters]);

  useEffect(() => {
    if (clientCode) {
      setContext({ clientCode });
      setFetching(false);
      return;
    }
    if (userId) {
      const loadUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          if (!ucc || String(ucc).trim() === "" || String(ucc) === "Reject") {
            Swal.fire("Error", "ClientCode (UCC) not available.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({ clientCode: String(ucc).trim() });
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      loadUser();
      return;
    }
    setError("ClientCode is required. Open from Reports or Order Status.");
    setFetching(false);
  }, [clientCode, userId, navigate]);

  useEffect(() => {
    const loadSchemes = async () => {
      try {
        const res = await getSchemes();
        if (res.data?.status === 1 && Array.isArray(res.data?.data)) {
          setSchemes(res.data.data);
        }
      } catch (err) {
        console.warn("Scheme list load failed:", err);
      }
    };
    loadSchemes();
  }, []);

  useEffect(() => {
    if (!context?.clientCode) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      const result = await fetchRedemptions();
      setLoading(false);
      if (result && Array.isArray(result)) {
        setRedemptions(result);
      } else {
        setError("Could not fetch redemption statement.");
      }
    };

    load();
  }, [context?.clientCode, filters, fetchRedemptions]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setFilterErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    if (!filters.fromDate) err.fromDate = "From Date is required";
    if (!filters.toDate) err.toDate = "To Date is required";
    if (filters.fromDate && filters.toDate) {
      const from = new Date(filters.fromDate);
      const to = new Date(filters.toDate);
      if (from > to) err.toDate = "To Date must be >= From Date";
    }
    setFilterErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError(null);
    const result = await fetchRedemptions();
    setLoading(false);
    if (result && Array.isArray(result)) setRedemptions(result);
    else setError("Could not fetch redemption statement.");
  };

  if (fetching || (!context && !error)) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <i className="fas fa-exclamation-triangle text-warning fa-3x mb-3" />
              <h5>Client Code Required</h5>
              <p className="text-muted mb-3">{error}</p>
              <Link to="/user" className="btn btn-primary">Back to Users</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">Redemption Statement</li>
              </ol>
            </nav>
            <h5>Redemption Statement</h5>
            <p className="text-muted mb-0">Client: <strong>{context?.clientCode}</strong></p>
          </div>

          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "24px",
              border: "1px solid #e6eef0",
            }}
          >
            <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
              Filters
            </h6>
            <form onSubmit={handleApply} className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label">Client Code</label>
                <input
                  type="text"
                  className="form-control"
                  value={context?.clientCode || ""}
                  readOnly
                  style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">From Date <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className={`form-control ${filterErrors.fromDate ? "is-invalid" : ""}`}
                  name="fromDate"
                  value={filters.fromDate}
                  onChange={handleFilterChange}
                />
                {filterErrors.fromDate && <div className="invalid-feedback">{filterErrors.fromDate}</div>}
              </div>
              <div className="col-md-3">
                <label className="form-label">To Date <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className={`form-control ${filterErrors.toDate ? "is-invalid" : ""}`}
                  name="toDate"
                  value={filters.toDate}
                  onChange={handleFilterChange}
                />
                {filterErrors.toDate && <div className="invalid-feedback">{filterErrors.toDate}</div>}
              </div>
              <div className="col-md-3">
                <label className="form-label">Scheme (Optional)</label>
                <select
                  className="form-select"
                  name="schemeCd"
                  value={filters.schemeCd}
                  onChange={handleFilterChange}
                >
                  <option value="">All Schemes</option>
                  {schemes.map((s) => (
                    <option key={s.schemeCd || s.id} value={s.schemeCd || s.id}>({s.schemeCd || s.id}) {s.schemeName || s.schemes || s.schemeCd}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm" /> : "Apply"}
                </button>
              </div>
            </form>

            {loading && redemptions.length === 0 && (
              <div className="text-center py-5">
                <span className="spinner-border spinner-border-lg text-primary mb-3" role="status" />
                <p>Fetching redemption statement...</p>
              </div>
            )}

            {error && redemptions.length === 0 && !loading && (
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-circle me-2" />
                {error}
              </div>
            )}

            {!loading && redemptions.length === 0 && !error && (
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2" />
                No redemptions found for selected period.
              </div>
            )}

            {redemptions.length > 0 && (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Order No</th>
                      <th>Scheme Name</th>
                      <th>Folio No</th>
                      <th>Redemption Amount</th>
                      <th>Units Redeemed</th>
                      <th>NAV</th>
                      <th>NAV Date</th>
                      <th>Payout Date</th>
                      <th>Credited Bank Account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {redemptions.map((r, i) => (
                      <tr key={r.orderNo + i}>
                        <td>{r.orderNo}</td>
                        <td>{r.schemeName}</td>
                        <td>{r.folioNo}</td>
                        <td>{r.redemptionAmount ? `₹${Number(r.redemptionAmount).toLocaleString()}` : "-"}</td>
                        <td>{r.unitsRedeemed || "-"}</td>
                        <td>{r.nav || "-"}</td>
                        <td>{r.navDate}</td>
                        <td>{r.payoutDate}</td>
                        <td>{r.creditedBankAccount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="d-flex gap-2 mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
              <Link to="/user/order-status" state={{ clientCode: context?.clientCode }} className="btn btn-outline-primary">Order Status</Link>
              <Link to="/user/allotment-statement" state={{ clientCode: context?.clientCode }} className="btn btn-outline-primary">Allotment Statement</Link>
              <Link to="/user" className="btn btn-secondary">Back to Users</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
